"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { ArrowLeft, Lock, Sparkles, AlertCircle } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ScenarioScript, Dialogue } from "@/lib/scenarioScript"
import { useTranslation } from "react-i18next"
import { PERSONAS_DATA } from "@/data/personas_data"

type GamePhase =
  | "OPENING"
  | "CHOICE_1"
  | "FAILURE"
  | "BAD_ENDING"
  | "READING"
  | "REWIND"
  | "CHOICE_2"
  | "SUCCESS"
  | "END"

export function GameplayScreen({ script }: { script: ScenarioScript }) {
  const { t, i18n } = useTranslation()
  const [phase, setPhase] = useState<GamePhase>("OPENING")
  const [currentLineIndex, setCurrentLineIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [canProceed, setCanProceed] = useState(true)
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null)
  const typingIntervalRef = useRef<any>(null)
  const [lastRevealAt, setLastRevealAt] = useState<number | null>(null)

  const rewindDialogue: Dialogue[] = useMemo(() => ([
    { type: "narrator", text: t("gameplay.rewind1") },
    { type: "narrator", text: t("gameplay.rewind2") },
    { type: "narrator", text: t("gameplay.rewind3") },
  ]), [t])

  const getCurrentDialogue = (): Dialogue[] => {
    // Base sequence by phase
    let seq: Dialogue[] = []
    switch (phase) {
      case "OPENING":
        seq = script.openingDialogues || []
        break
      case "FAILURE":
        if (!selectedChoiceId) return []
        const opt = script.options.survivalOptions.find(o => o.id === selectedChoiceId)
        seq = opt ? opt.followUp : []
        break
      case "REWIND":
        seq = rewindDialogue
        break
      case "SUCCESS":
        seq = script.options.creativeOption.followUp || []
        break
      default:
        seq = []
    }

    // Dedupe consecutive identical text lines to prevent duplicate rendering
    const result: Dialogue[] = []
    let prevNorm = ""
    for (const line of seq) {
      const full = line.textKey ? t(line.textKey) : (line.text || "")
      const norm = full.replace(/\s+/g, " ").trim()
      if (norm && norm === prevNorm) {
        continue
      }
      prevNorm = norm
      result.push(line)
    }
    return result
  }

  const currentDialogue = getCurrentDialogue()
  const currentLine = currentDialogue[currentLineIndex]

  useEffect(() => {
    if (!currentLine) return

    setIsTyping(true)
    setDisplayedText("")
    setCanProceed(false)

    let charIndex = 0
    const text = currentLine.textKey ? t(currentLine.textKey) : currentLine.text
    const typingSpeed = 30

    const typeInterval = setInterval(() => {
      if (charIndex < text.length) {
        setDisplayedText(text.slice(0, charIndex + 1))
        charIndex++
      } else {
        setIsTyping(false)
        setCanProceed(true)
        clearInterval(typeInterval)
      }
    }, typingSpeed)

    typingIntervalRef.current = typeInterval

    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current)
        typingIntervalRef.current = null
      }
    }
  }, [currentLineIndex, phase, t, i18n.language])

  const handleScreenClick = () => {
    if (phase === "CHOICE_1" || phase === "BAD_ENDING" || phase === "READING" || phase === "CHOICE_2") {
      return
    }
    if (isTyping) {
      if (currentLine) {
        const fullText = currentLine.textKey ? t(currentLine.textKey) : currentLine.text
        setDisplayedText(fullText)
        setIsTyping(false)
        setCanProceed(true)
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current)
          typingIntervalRef.current = null
        }
        setLastRevealAt(Date.now())
      }
      return
    }
    // Guard against double tap (mobile): don't advance immediately after reveal
    if (canProceed) {
      if (lastRevealAt && Date.now() - lastRevealAt < 250) {
        return
      }
      if (currentLineIndex < currentDialogue.length - 1) {
        setCurrentLineIndex((prev) => prev + 1)
      } else {
        handlePhaseTransition()
      }
    }
  }

  const handlePhaseTransition = () => {
    if (phase === "OPENING") {
      setPhase("CHOICE_1")
    } else if (phase === "FAILURE") {
      setPhase("BAD_ENDING")
    } else if (phase === "REWIND") {
      setPhase("CHOICE_2")
    } else if (phase === "SUCCESS") {
      setPhase("END")
    }
  }

  const handleNegativeChoice = (choiceId: string) => {
    setSelectedChoiceId(choiceId)
    setCurrentLineIndex(0)
    setPhase("FAILURE")
  }

  const handleRewriteStory = () => {
    setPhase("READING")
  }

  const handleUnlockNewStory = () => {
    setCurrentLineIndex(0)
    setPhase("REWIND")
  }

  const handleAwakeningChoice = () => {
    setCurrentLineIndex(0)
    setPhase("SUCCESS")
  }

  const navigate = useNavigate()

  const getPersonaDisplay = (archetype: string | undefined): string => {
    if (!archetype) return ""
    const persona = PERSONAS_DATA[archetype]
    if (!persona) return archetype
    const lang = i18n.language || "en"
    const isZh = lang.startsWith("zh")
    return isZh ? persona.name : (persona.englishName || persona.name)
  }

  return (
    <div
      className="relative mt-12 md:mt-14 mx-auto flex w-full max-w-[900px] md:max-w-[900px] h-[calc(90vh-8rem)] md:h-[calc(120vh-20rem)] flex-col cursor-pointer overflow-hidden rounded-xl shadow-xl"
      onClick={handleScreenClick}
    >
      <div className="absolute inset-0 z-0">
        <img
          src={script.sceneImage}
          alt={script.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <AnimatePresence>
        {phase === "FAILURE" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.7, 0.4, 0.7, 0.5],
              backgroundColor: (() => {
                const opt = selectedChoiceId ? script.options.survivalOptions.find(o => o.id === selectedChoiceId) : undefined
                const effect = opt?.visualEffect || "dark"
                return (effect === "red" || effect === "dark_red")
                  ? ["rgba(127, 29, 29, 0)", "rgba(127, 29, 29, 0.7)", "rgba(127, 29, 29, 0.5)"]
                  : ["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.7)", "rgba(0, 0, 0, 0.6)"]
              })(),
            }}
            transition={{ duration: 2, times: [0, 0.3, 0.5, 0.7, 1] }}
            className="pointer-events-none absolute inset-0 z-30 rounded-xl"
          />
        )}
      </AnimatePresence>

      {/* 成功阶段视觉特效（根据 creativeOption.visualEffect 渲染） */}
      <AnimatePresence>
        {phase === "SUCCESS" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.6, 0.4, 0.6, 0.5],
              backgroundColor: (() => {
                const effect = script.options.creativeOption.visualEffect || "green"
                if (effect === "red") {
                  return [
                    "rgba(127, 29, 29, 0)",
                    "rgba(127, 29, 29, 0.6)",
                    "rgba(127, 29, 29, 0.5)",
                  ]
                }
                if (effect === "dark") {
                  return [
                    "rgba(0, 0, 0, 0)",
                    "rgba(0, 0, 0, 0.6)",
                    "rgba(0, 0, 0, 0.5)",
                  ]
                }
                if (effect === "blue") {
                  // blue 成功光效（近似 blue-700）
                  return [
                    "rgba(29, 78, 216, 0)",
                    "rgba(29, 78, 216, 0.6)",
                    "rgba(29, 78, 216, 0.5)",
                  ]
                }
                if (effect === "indigo") {
                  // indigo 成功光效（近似 indigo-700）
                  return [
                    "rgba(67, 56, 202, 0)",
                    "rgba(67, 56, 202, 0.6)",
                    "rgba(67, 56, 202, 0.5)",
                  ]
                }
                if (effect === "violet") {
                  // violet 成功光效（近似 violet-700）
                  return [
                    "rgba(109, 40, 217, 0)",
                    "rgba(109, 40, 217, 0.6)",
                    "rgba(109, 40, 217, 0.5)",
                  ]
                }
                if (effect === "yellow") {
                  // yellow 成功光效（近似 yellow-600）
                  return [
                    "rgba(202, 138, 4, 0)",
                    "rgba(202, 138, 4, 0.6)",
                    "rgba(202, 138, 4, 0.5)",
                  ]
                }
                if (effect === "orange") {
                  // orange 成功光效（近似 orange-600）
                  return [
                    "rgba(234, 88, 12, 0)",
                    "rgba(234, 88, 12, 0.6)",
                    "rgba(234, 88, 12, 0.5)",
                  ]
                }
                // green 成功光效（近似 emerald-700）
                return [
                  "rgba(4, 120, 87, 0)",
                  "rgba(4, 120, 87, 0.6)",
                  "rgba(4, 120, 87, 0.5)",
                ]
              })(),
            }}
            transition={{ duration: 2, times: [0, 0.3, 0.5, 0.7, 1] }}
            className="pointer-events-none absolute inset-0 z-30 rounded-xl"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "FAILURE" && (
          <motion.div
            animate={{
              x: [0, -10, 10, -10, 10, 0],
              y: [0, -5, 5, -5, 5, 0],
            }}
            transition={{ duration: 0.5, repeat: 3 }}
            className="absolute inset-0 z-20"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "READING" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-30 rounded-xl bg-black/40 backdrop-blur-sm backdrop-grayscale"
          />
        )}
      </AnimatePresence>

      {/* 返回按钮 */}
      <div className="absolute left-4 top-4 z-10">
        <button
          className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm transition-all hover:bg-black/80"
          onClick={(e) => { e.stopPropagation(); navigate("/life-editor"); }}
          aria-label={t("gameplay.backToEditorAria")}
        >
          <ArrowLeft className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* 场景标题 */}
      <div className="absolute left-4 right-4 top-16 z-10">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-bold text-2xl text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
        >
          {script.titleKey ? t(script.titleKey) : script.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-1 text-white/90 text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
        >
        </motion.p>
      </div>

      {(phase === "OPENING" || phase === "FAILURE" || phase === "REWIND" || phase === "SUCCESS") && (
        <div className="flex-1 flex flex-col justify-end pb-12 md:pb-2" />
      )}

      <AnimatePresence mode="wait">
        {currentLine && (phase === "OPENING" || phase === "FAILURE" || phase === "REWIND" || phase === "SUCCESS") && (
          <motion.div
            key={`${phase}-${currentLineIndex}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="z-20 px-4"
          >
            <div className="mx-auto max-w-3xl mb-[4%] md:mb-[6%]">
              <div className="relative">
                {canProceed && !isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
                    className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full -mt-1 z-10"
                  >
                    <span className="text-white/80 text-xs md:text-sm bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">{t("gameplay.continueHint")}</span>
                  </motion.div>
                )}

                <div
                  className={`
                    relative rounded-xl border p-5 shadow-2xl backdrop-blur-md min-h-[120px] flex items-start
                    ${currentLine.type !== "npc" 
                      ? "bg-black/60 border-white/10" 
                      : "bg-slate-900/80 border-amber-500/30"}
                  `}
                >
                  {currentLine.type === "npc" && (currentLine.speakerKey || currentLine.speaker) && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="absolute top-0 left-4 -translate-y-full -mt-7">
                      <div className="inline-block rounded-t-lg bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-0.5 font-medium text-xs md:text-sm text-white shadow-lg">
                        {currentLine.speakerKey ? t(currentLine.speakerKey) : currentLine.speaker}
                      </div>
                    </motion.div>
                  )}
                  <p className={`text-base md:text-lg leading-relaxed w-full ${currentLine.type !== "npc" ? "text-gray-300 italic text-center mt-2" : "text-white"}`}>
                    {displayedText}
                    {isTyping && <span className="animate-pulse">_</span>}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "BAD_ENDING" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 rounded-xl"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
            >
              <Card className="w-[95%] max-w-sm md:max-w-md mx-auto border-2 border-red-500 bg-slate-900/95 p-4 md:p-6 shadow-2xl max-h-[85%] overflow-y-auto flex flex-col justify-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  className="mb-4 flex justify-center"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                </motion.div>
                <h2 className="mb-2 text-center font-bold text-xl md:text-2xl text-red-500">{t("gameplay.badEnding.title")}</h2>
                <p className="mb-6 text-center text-gray-300 text-base md:text-lg">{t("gameplay.badEnding.desc")}</p>

                <div className="relative">
                  <div className="absolute -inset-4 animate-pulse rounded-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 blur-xl" />
                  <Button
  size="lg"
  // 修改点：
  // 1. h-auto whitespace-normal: 高度自适应，允许换行
  // 2. flex-col: 图标和文字垂直排列，节省横向空间
  // 3. px-2 text-sm: 调整内边距和字体大小以适应 80% 宽度的卡片
  className="relative w-full h-auto min-h-[3.5rem] whitespace-normal flex flex-col items-center justify-center text-center border-2 border-amber-400 bg-gradient-to-r from-amber-500 to-yellow-500 py-4 px-2 font-bold text-m md:text-lg text-white shadow-lg transition-all hover:scale-105 hover:from-amber-400 hover:to-yellow-400"
  onClick={(e) => { e.stopPropagation(); handleRewriteStory(); }}
>
  {/* 图标加个 mb-1 与文字隔开一点，并防止挤压(shrink-0) */}
  <span className="leading-tight">{t("gameplay.badEnding.cta")}</span>
</Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "READING" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 flex items-center justify-center p-4 rounded-xl"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-full max-w-lg"
            >
              <Card className="relative w-full max-w-sm md:max-w-lg mx-auto border-2 border-amber-500/50 bg-slate-900/95 p-4 md:p-6 shadow-2xl">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-bold text-xl text-white">{script.options.directorNote.titleKey ? t(script.options.directorNote.titleKey) : script.options.directorNote.title}</h3>
                </div>
                <div className="mb-4 max-h-64 overflow-y-auto rounded-lg bg-black/40 p-4">
                  <p className="leading-relaxed text-gray-300 text-sm whitespace-pre-wrap">
                    {script.options.directorNote.contentKey ? t(script.options.directorNote.contentKey) : script.options.directorNote.content}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="w-full bg-amber-500 py-5 md:py-6 font-medium text-white transition-all hover:scale-[1.02] hover:bg-amber-600"
                  onClick={handleUnlockNewStory}
                >
                  {t("gameplay.reading.cta")}
                </Button>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 奖励模式已移除 */}

      <AnimatePresence>
        {phase === "END" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
            >
              <Card className="w-full max-w-sm md:max-w-md mx-auto border-2 border-amber-500 bg-slate-900/95 p-4 md:p-6 shadow-2xl">
                <div className="mb-4 flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/20">
                    <Sparkles className="h-8 w-8 text-amber-400" />
                  </div>
                </div>
                <h2 className="mb-2 text-center font-bold text-xl md:text-2xl text-white">{t("gameplay.end.title")}</h2>
                <p className="mb-6 text-center text-gray-300 text-sm md:text-base">{t("gameplay.end.desc")}</p>
                <div className="space-y-3">
                  <Button
                    size="lg"
                    className="w-full bg-amber-500 py-5 md:py-6 font-medium text-white transition-all hover:scale-[1.02] hover:bg-amber-600"
                    onClick={() => navigate("/life-editor")}
                  >
                    {t("gameplay.end.backToList")}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-amber-500/60 text-black"
                    onClick={() => {
                      setSelectedChoiceId(null)
                      setCurrentLineIndex(0)
                      setDisplayedText("")
                      setIsTyping(false)
                      setCanProceed(true)
                      setPhase("OPENING")
                    }}
                  >
                    {t("gameplay.end.retry")}
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-x-0 bottom-0 z-30 px-4 pb-4">
        <div className="mx-auto max-w-3xl">
          <AnimatePresence mode="wait">
            {phase === "CHOICE_1" && (
              <motion.div
                key="choice-1"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-3"
              >
                <p className="mb-4 text-center text-white/70 text-sm">{t("gameplay.choice1.prompt")}</p>
                <Button
                  size="lg"
                  className="w-full h-auto min-h-[4rem] whitespace-normal flex flex-col items-center justify-center text-center px-4 border-2 border-blue-400/50 bg-gradient-to-r from-blue-600/80 to-blue-500/80 py-4 backdrop-blur-sm transition-all hover:scale-[1.02] hover:from-blue-500/90 hover:to-blue-400/90"
                  onClick={() => handleNegativeChoice(script.options.survivalOptions[0].id)}
                >
                  <span className="font-medium text-lg text-white leading-tight">
                    {script.options.survivalOptions[0].labelKey ? t(script.options.survivalOptions[0].labelKey) : script.options.survivalOptions[0].label}
                  </span>
                  <span className="mt-1 text-blue-200 text-sm">
                    ({getPersonaDisplay(script.options.survivalOptions[0].archetype)})
                  </span>
                </Button>
                <Button
                  size="lg"
                  className="w-full h-auto min-h-[4rem] whitespace-normal flex flex-col items-center justify-center text-center px-4 border-2 border-red-400/50 bg-gradient-to-r from-red-600/80 to-red-500/80 py-4 backdrop-blur-sm transition-all hover:scale-[1.02] hover:from-red-500/90 hover:to-red-400/90"
                  onClick={() => handleNegativeChoice(script.options.survivalOptions[1].id)}
                >
                  <span className="font-medium text-lg text-white leading-tight">
                    {script.options.survivalOptions[1].labelKey ? t(script.options.survivalOptions[1].labelKey) : script.options.survivalOptions[1].label}
                  </span>
                  <span className="mt-1 text-red-200 text-sm">
                    ({getPersonaDisplay(script.options.survivalOptions[1].archetype)})
                  </span>
                </Button>
              </motion.div>
            )}

            {phase === "CHOICE_2" && (
              <motion.div
                key="choice-2"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-3"
              >
                <p className="mb-4 text-center font-medium text-amber-400 text-sm">{t("gameplay.choice2.unlocked")}</p>
                <Button
                  size="lg"
                  disabled
                  className="w-full h-auto min-h-[4rem] whitespace-normal flex flex-col items-center justify-center text-center px-4 cursor-not-allowed border-2 border-gray-600/30 bg-gray-800/40 py-4 opacity-30"
                >
                  <span className="text-gray-500 text-lg leading-tight">
                    {script.options.survivalOptions[0].labelKey ? t(script.options.survivalOptions[0].labelKey) : script.options.survivalOptions[0].label}
                  </span>
                  <span className="mt-1 text-gray-600 text-sm">
                    ({getPersonaDisplay(script.options.survivalOptions[0].archetype)})
                  </span>
                </Button>
                <Button
                  size="lg"
                  disabled
                  className="w-full h-auto min-h-[4rem] whitespace-normal flex flex-col items-center justify-center text-center px-4 cursor-not-allowed border-2 border-gray-600/30 bg-gray-800/40 py-4 opacity-30"
                >
                  <span className="text-gray-500 text-lg leading-tight">
                    {script.options.survivalOptions[1].labelKey ? t(script.options.survivalOptions[1].labelKey) : script.options.survivalOptions[1].label}
                  </span>
                  <span className="mt-1 text-gray-600 text-sm">
                    ({getPersonaDisplay(script.options.survivalOptions[1].archetype)})
                  </span>
                </Button>
                <motion.div
                  animate={{
                    boxShadow: [
                      "0 0 20px rgba(251, 191, 36, 0.5)",
                      "0 0 40px rgba(251, 191, 36, 0.8)",
                      "0 0 20px rgba(251, 191, 36, 0.5)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  className="rounded-lg"
                >
                  <Button
                    size="lg"
                    className="w-full h-auto min-h-[4rem] whitespace-normal flex flex-col items-center justify-center text-center px-4 border-2 border-amber-400 bg-gradient-to-r from-amber-500 to-yellow-500 py-4 font-bold text-lg text-white shadow-lg transition-all hover:scale-[1.02] hover:from-amber-400 hover:to-yellow-400"
                    onClick={(e) => { e.stopPropagation(); handleAwakeningChoice(); }}
                  >
                    <span className="leading-tight">{script.options.creativeOption.labelKey ? t(script.options.creativeOption.labelKey) : script.options.creativeOption.label}</span>
                    <span className="mt-1 text-amber-100 text-sm">({getPersonaDisplay(script.options.creativeOption.archetype)})</span>
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

